import { useEffect, useRef } from "react";
import {
  AlertChime,
  createFileTonePlayer,
  createWebAudioTonePlayer,
  planChime,
} from "../platform/alertChime";

/** Pixabay, owner-chosen 2026-09-14. Pixabay licence: commercial use, no attribution. */
const ORDER_WAITING_SOUND = "/sounds/order-waiting.mp3";

/** Owner's ruling 2026-10-16 (was 60 s, ruled 2026-09-13): sound again every 15 seconds until somebody takes it. */
const REPEAT_MS = 15_000;

/** Gestures that count as "this page has been interacted with". */
const UNLOCK_EVENTS: Array<keyof WindowEventMap> = [
  "pointerdown",
  "touchstart",
  "keydown",
];

/**
 * Sounds the "order is waiting" chime.
 *
 * TWO INPUTS, DELIBERATELY. `active` says whether anything trustworthy is
 * waiting and so owns the minute repeat; `arrivals` says what is NEW and so
 * owns every deliberate sound. Driving both from one boolean is what made a
 * second order arrive in silence — `waitingCount > 0` does not change when the
 * count goes 1 → 2, so the effect never re-ran and only the leftover repeat was
 * heard, which a florist reads as the same order still waiting.
 *
 * WHAT to do with those two inputs is `planChime`, a pure function with its own
 * tests, because that decision is where the bug lived. This hook only applies
 * the plan.
 *
 * THE UNLOCK LISTENER IS THE OTHER HALF. A browser will not make a sound until
 * the page has been touched, and this runs on a screen nobody has touched —
 * that is its whole premise. So every tap anywhere on the kiosk quietly unlocks
 * the audio, hours before there is anything to announce. Without it the first
 * alert of the day is silent and looks identical to one that worked.
 */
export function useAlertChime(
  active: boolean,
  arrivals: readonly string[],
  onSounded: () => void,
): void {
  const chimeRef = useRef<AlertChime | null>(null);

  /*
   * Built in an effect, not in the render body. StrictMode double-invokes both
   * render and `useState` initialisers, so either would construct a second
   * chime (and its two tone players) and drop it on the floor. An effect runs
   * once per mount and cleans up, so exactly one instance is ever live.
   */
  useEffect(() => {
    chimeRef.current = new AlertChime(
      // The owner's chosen recording, with the synth behind it so a missing
      // asset degrades to a quieter noise rather than to silence.
      createFileTonePlayer(
        typeof Audio === "undefined"
          ? null
          : () => new Audio(ORDER_WAITING_SOUND),
        createWebAudioTonePlayer(),
      ),
      {
        setInterval: (handler, ms) => window.setInterval(handler, ms),
        clearInterval: (id) => window.clearInterval(id),
      },
      REPEAT_MS,
    );
    return () => {
      // Leaving the screen must never leave a timer ringing into an empty room.
      chimeRef.current?.stop();
      chimeRef.current = null;
    };
  }, []);

  useEffect(() => {
    const unlock = () => chimeRef.current?.unlock();
    for (const event of UNLOCK_EVENTS) {
      window.addEventListener(event, unlock, { passive: true });
    }
    return () => {
      for (const event of UNLOCK_EVENTS) {
        window.removeEventListener(event, unlock);
      }
    };
  }, []);

  useEffect(() => {
    const chime = chimeRef.current;
    if (!chime) return;
    const plan = planChime(active, arrivals.length);
    // Arm before pulsing: `pulse` refuses to install a clock of its own.
    if (plan.arm) chime.start();
    if (plan.silence) chime.stop();
    if (plan.pulse) chime.pulse();
    if (plan.acknowledge) onSounded();
  }, [active, arrivals, onSounded]);
}
